import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate, useParams } from 'react-router-dom'

import { caseAccessHttp } from '@/services/caseAccess'
import SalaryModulePanel, { type SalaryPanelClient } from '@/pages/assessments/SalaryModulePanel'
import CommonLedgerPanel, {
  type CommonLedgerPanelClient,
} from '@/pages/assessments/CommonLedgerPanel'
import CollaborationPanel, {
  type CollaborationClient,
} from '@/pages/assessments/CollaborationPanel'
import SectionAttestationPanel, {
  type AttestationClient,
} from '@/pages/assessments/SectionAttestationPanel'
import CapitalGainsPanel, {
  type CapitalGainsPanelClient,
} from '@/pages/assessments/CapitalGainsPanel'
import BusinessModulePanel, {
  type BusinessPanelClient,
} from '@/pages/assessments/BusinessModulePanel'
import InternationalTaxPanel, {
  type InternationalTaxPanelClient,
} from '@/pages/assessments/InternationalTaxPanel'
import ReviewReadinessPanel, {
  type ReviewReadinessClient,
} from '@/pages/assessments/ReviewReadinessPanel'

export default function CaseAccessWorkspacePage() {
  const { caseId = '' } = useParams()
  const queryClient = useQueryClient()
  const completion = caseAccessHttp.load()
  const validSession =
    completion &&
    completion.case_id === caseId &&
    new Date(completion.expires_at).getTime() > Date.now()
  const workspace = useQuery({
    queryKey: ['bounded-case-workspace', caseId],
    queryFn: () => caseAccessHttp.workspace(completion!),
    enabled: Boolean(validSession),
  })
  const applicability = useQuery({
    queryKey: ['bounded-case-applicability', caseId],
    queryFn: () => caseAccessHttp.applicability(completion!),
    enabled: Boolean(validSession && completion!.allowed_actions.includes('VIEW_CASE')),
  })

  if (!validSession) return <Navigate to="/case-invite" replace />
  if (workspace.isLoading) return <div className="p-6">Loading your case…</div>
  if (workspace.isError || !workspace.data)
    return (
      <div className="p-6">
        <div className="alert alert-error" role="alert">
          This bounded case session is unavailable, expired, or revoked.
        </div>
      </div>
    )
  const salaryClient: SalaryPanelClient = {
    salaryModule: () => caseAccessHttp.salaryModule(completion),
    createSalaryEmployer: (_caseId, input) =>
      caseAccessHttp.createSalaryEmployer(completion, input),
    createSalaryEmployment: (_caseId, input) =>
      caseAccessHttp.createSalaryEmployment(completion, input),
    createSalaryComponent: (_caseId, input) =>
      caseAccessHttp.createSalaryComponent(completion, input),
    createSalaryHraPeriod: (_caseId, input) =>
      caseAccessHttp.createSalaryHraPeriod(completion, input),
    createSalaryLtaJourney: (_caseId, input) =>
      caseAccessHttp.createSalaryLtaJourney(completion, input),
    createSalaryReliefItem: (_caseId, input) =>
      caseAccessHttp.createSalaryReliefItem(completion, input),
  }
  const commonLedgerClient: CommonLedgerPanelClient = {
    commonLedger: () => caseAccessHttp.commonLedger(completion),
    createCommonLedgerResource: (_caseId, path, input) =>
      caseAccessHttp.createCommonLedgerResource(completion, path, input),
    disposeContinuityItem: (_caseId, itemId, input) =>
      caseAccessHttp.disposeContinuityItem(completion, itemId, input),
    updateCommonLedgerEvidence: (_caseId, requirementKey, input) =>
      caseAccessHttp.updateCommonLedgerEvidence(completion, requirementKey, input),
  }
  const collaborationClient: CollaborationClient = {
    collaboration: () => caseAccessHttp.collaboration(completion),
    respondToQuery: (_caseId, queryId, input) =>
      caseAccessHttp.respondToQuery(completion, queryId, input),
  }
  const attestationClient: AttestationClient = {
    attestation: (_caseId, sectionKey) => caseAccessHttp.attestation(completion, sectionKey),
    confirmSection: (_caseId, sectionKey, input) =>
      caseAccessHttp.confirmSection(completion, sectionKey, input),
    confirmHighRiskItem: (_caseId, sectionKey, itemKey, input) =>
      caseAccessHttp.confirmHighRiskItem(completion, sectionKey, itemKey, input),
  }
  const capitalClient: CapitalGainsPanelClient = {
    capitalWorkspace: () => caseAccessHttp.capitalWorkspace(completion),
    brokerImportRows: (_caseId, batchId, page, pageSize) =>
      caseAccessHttp.brokerImportRows(completion, batchId, page, pageSize),
    flagBrokerImportRow: (_caseId, rowId, input) =>
      caseAccessHttp.flagBrokerImportRow(completion, rowId, input),
  }
  const businessClient: BusinessPanelClient = {
    businessWorkspace: () => caseAccessHttp.businessWorkspace(completion),
    createBusinessResource: (_caseId, path, input) =>
      caseAccessHttp.createBusinessResource(completion, path, input),
    evaluateBusiness: (_caseId, input) => caseAccessHttp.evaluateBusiness(completion, input),
  }
  const internationalClient: InternationalTaxPanelClient = {
    internationalWorkspace: () => caseAccessHttp.internationalWorkspace(completion),
    createInternationalResource: (_caseId, path, input) =>
      caseAccessHttp.createInternationalResource(completion, path, input),
    evaluateInternational: (_caseId, input) =>
      caseAccessHttp.evaluateInternational(completion, input),
  }
  const reviewClient: ReviewReadinessClient = {
    reviewWorkspace: () => caseAccessHttp.reviewWorkspace(completion),
    signFinalDeclaration: (_caseId, snapshotId, input) =>
      caseAccessHttp.signFinalDeclaration(completion, snapshotId, input),
    beginFinalDeclarationStepUp: email => caseAccessHttp.beginStepUp(completion, email),
    verifyFinalDeclarationStepUp: async (challengeId, otp) => {
      const refreshed = await caseAccessHttp.verifyStepUp(completion, challengeId, otp)
      Object.assign(completion, refreshed)
      caseAccessHttp.store(completion)
      return refreshed
    },
  }
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['bounded-case-workspace', caseId] })
  }
  const salaryAvailable = (workspace.data.sections ?? []).some(
    section => section.key === 'salary' && section.state === 'available'
  )
  return (
    <main className="mx-auto max-w-5xl space-y-4 p-4 sm:p-6">
      <header className="card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">Your tax preparation case</h1>
            <p className="text-sm text-text-secondary">
              Customer {workspace.data.case.customer_id} · {workspace.data.case.period_value}
            </p>
          </div>
          <span className="badge badge-primary">
            {completion.participant_kind === 'REPRESENTATIVE'
              ? 'Acting as representative'
              : 'Individual client'}
          </span>
        </div>
        {completion.participant_kind === 'REPRESENTATIVE' && (
          <p className="mt-3 text-sm">
            Your actions are recorded in your own identity, not as the principal.
          </p>
        )}
        <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-text-secondary">Allowed actions</dt>
            <dd>{completion.allowed_actions.join(', ')}</dd>
          </div>
          <div>
            <dt className="text-text-secondary">Access expires</dt>
            <dd>{new Date(completion.expires_at).toLocaleString()}</dd>
          </div>
        </dl>
      </header>
      <section className="card p-5" aria-labelledby="tasks-heading">
        <h2 id="tasks-heading" className="font-semibold">
          Case tasks
        </h2>
        <p className="mt-2 text-sm text-text-secondary">
          Only actions explicitly listed above are available. Confirmation and final declarations
          always remain with the principal.
        </p>
        {applicability.isLoading && <p className="mt-4">Loading applicability…</p>}
        {applicability.data && (
          <div className="mt-4 rounded border border-border p-4">
            <strong>Applicability questionnaire</strong>
            <p className="text-sm text-text-secondary">
              {applicability.data.answers.length} answers saved · routing{' '}
              {applicability.data.routing?.status ?? 'not evaluated'}
            </p>
          </div>
        )}
        {salaryAvailable && completion.allowed_actions.includes('VIEW_CASE') && (
          <SalaryModulePanel
            caseId={caseId}
            client={salaryClient}
            sourceType="CUSTOMER_MANUAL"
            canEdit={completion.allowed_actions.includes('ENTER_FACT')}
            onChanged={refresh}
          />
        )}
        {completion.allowed_actions.includes('VIEW_CASE') && (
          <CommonLedgerPanel
            caseId={caseId}
            client={commonLedgerClient}
            sourceType="CUSTOMER_MANUAL"
            canEdit={completion.allowed_actions.includes('ENTER_FACT')}
            onChanged={refresh}
          />
        )}
        {completion.allowed_actions.includes('VIEW_CASE') && (
          <CapitalGainsPanel
            caseId={caseId}
            client={capitalClient}
            sourceType="CUSTOMER_MANUAL"
            canImport={false}
            canFlag={completion.allowed_actions.includes('ENTER_FACT')}
            onChanged={refresh}
          />
        )}
        {completion.allowed_actions.includes('VIEW_CASE') && (
          <BusinessModulePanel
            caseId={caseId}
            client={businessClient}
            sourceType="CUSTOMER_MANUAL"
            canEdit={completion.allowed_actions.includes('ENTER_FACT')}
            onChanged={refresh}
          />
        )}
        {completion.allowed_actions.includes('VIEW_CASE') && (
          <InternationalTaxPanel
            caseId={caseId}
            client={internationalClient}
            sourceType="CUSTOMER_MANUAL"
            canEdit={completion.allowed_actions.includes('ENTER_FACT')}
            onChanged={refresh}
          />
        )}
        {completion.allowed_actions.includes('VIEW_CASE') && (
          <CollaborationPanel
            caseId={caseId}
            client={collaborationClient}
            mode="customer"
            canRespond={completion.allowed_actions.includes('RESPOND_QUERY')}
            onChanged={refresh}
          />
        )}
        {completion.allowed_actions.includes('VIEW_CASE') && (
          <SectionAttestationPanel
            caseId={caseId}
            sectionKeys={[
              ...new Set((workspace.data.modules ?? []).map(module => module.section_key)),
            ]}
            client={attestationClient}
            mode="customer"
            canAct={
              completion.participant_kind === 'PRINCIPAL' &&
              completion.allowed_actions.includes('CONFIRM_SECTION')
            }
            onChanged={refresh}
          />
        )}
        {completion.allowed_actions.includes('VIEW_CASE') && (
          <ReviewReadinessPanel caseId={caseId} client={reviewClient} onChanged={refresh} />
        )}
      </section>
    </main>
  )
}
